import React, { Suspense } from 'react';
// WEB-1988: прелоадер и граница падения — публичные (без Ant Design). Визард
// рендерится и в воронке покупателя, и в превью конструктора; кит-прелоадер и
// кабинетная AppErrorBoundary тянут в бандл покупателя весь antd.
import { PublicPreloader as Preloader } from '@/components/public/PublicPreloader';
import { PublicErrorBoundary } from '@/components/public/PublicErrorBoundary';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';

// WEB-744 §3 (_funnel-runtime.md): единый плеер — на COMPLETE рендерим пейволл
// воронки инлайн той же страницей (без редиректа/перезагрузки), переиспользуя
// PublicPaywallPage целиком (данные/чекаут/аналитика — уже реализованы там).
// Отложенная загрузка: PublicPaywall.tsx тянет @/api/stripe (Stripe.js, вызовы
// чекаута) — плееру lead-gen квиза этот бандл не нужен, а статический импорт
// сломал бы каждый тестовый набор, который не подменяет @/api/stripe (его
// относительный ./client использует import.meta, а обходит это только
// собственный набор PublicPaywall). Отложенность также значит, что наборы, не
// заходящие в ветку воронки, stripe.ts не требуют вовсе.
//
// WEB-1539: обёртка `lazyWithRetry`, а НЕ голый `lazy`. Довод выше объясняет
// выбор отложенной загрузки — но не отсутствие повторной попытки, а обёртка
// тоже грузит модуль отложенно. Без неё выкат новой сборки (хешированный файл
// пейволла на CDN исчезает, у посетителя открыта старая страница) оставлял
// человека на вечном прелоадере ровно в точке оплаты: ни сообщения, ни
// кнопки, ни второй попытки. Все остальные публичные точки входа
// (router/index.tsx) защищены этой обёрткой с WEB-1336 — эта была пропущена.
// Ключ защёлки задан ЯВНО и ТОТ ЖЕ, что у грелки в PublicHostRouter: это
// один и тот же файл, значит и защёлка у него обязана быть одна
// (свежий взгляд пачки 36, Ф1).
//
// WEB-3402: вынесено из QuizScreenWizard.tsx вместе с шагом после квиза;
// загрузка по-прежнему отложенная, ключ защёлки прежний.
const PublicPaywallPage = lazyWithRetry(
  () => import('@/pages/PublicPaywall'),
  'page:public-paywall',
);

/**
 * Шаг после квиза: встроенный пейволл воронки либо заглушка «Спасибо».
 *
 * Функция отрисовки, а не компонент: плеер возвращает её результат как
 * раньше возвращал ту же разметку — дерево узлов не меняется.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения.
 * Параметры позиционные, а не объектом: имена ключей объекта минификатор не
 * сжимает, а запас веса первого экрана покупателя — десятки байт.
 *
 * @param stepNumber номер последнего показанного шага пути посетителя, с единицы
 */
export function renderQuizCompletedStep(
  funnel: { paywallSlug: string; funnelId: string | null } | null | undefined,
  projectId: string,
  servedStale: boolean | undefined,
  language: string | undefined,
  stepNumber: number,
  stylePackId: string | undefined,
): React.ReactElement {
  // WEB-744 §3 (ратиф. PM 2026-07-19): единый плеер — funnel-квиз рендерит
  // пейволл воронки инлайн той же страницей, БЕЗ редиректа/перезагрузки.
  // Переиспользуем PublicPaywallPage целиком (данные/чекаут/трекинг уже
  // реализованы там) через тот же routeOverride-механизм, которым его
  // подключает PublicHostRouter. visitorId (localStorage)/sessionId
  // (sessionStorage) — общие для таба и переживают переход без изменений,
  // funnel_journey не форкается (AC §3).
  if (funnel?.paywallSlug) {
    // TODO(WEB-744 этап 2, _funnel-runtime.md §3): history.replaceState на
    // публичный путь пейволла — однозначного URL в hosting-режиме
    // PublicHostRouter пока нет (домен на воронку — отдельный атом), поэтому
    // адресная строка на этапе 1 намеренно не трогается.
    return (
      // WEB-1539 п.2: граница ошибок ВОКРУГ ожидания — второй заслон против
      // вечного прелоадера как класса. Повторная попытка (lazyWithRetry)
      // спасает от типового случая «файл исчез после выката»; если и она не
      // помогла (настоящий аутаж CDN, либо срыв уже после перезагрузки),
      // ожидание Suspense не заканчивается никогда и человек просто уходит.
      // Граница переводит это в понятное сообщение с кнопкой «Обновить
      // страницу». WEB-1988: это `PublicErrorBoundary` — та же граница по
      // сути, что кабинетная `AppErrorBoundary`, и текст она берёт из ТОГО
      // ЖЕ словаря (crashTitle/crashSubtitle/reloadPage), так что разъехаться
      // формулировкам не с чего; отчёт о сбое тот же (счётчик админки, а
      // Sentry — если он поднялся). Разница одна: кабинетная граница рисует
      // экран компонентами Ant Design, а визард показывается покупателю
      // воронки, куда antd не приезжает вовсе. Границей ниже по дереву
      // сбой перехвачен рядом с местом — остальная страница квиза остаётся
      // на экране, а не заменяется целиком.
      <PublicErrorBoundary>
        <Suspense fallback={<Preloader data-testid="funnel-paywall-loading" />}>
          {/* WEB-871 (3rd try): `embedded` is the explicit signal that this
              mount lives inside THIS component's own public-quiz-main/
              public-quiz-container обёртки — PublicPaywallPage must not
              render its own main/container pair on top of them (that
              double-frame was root cause #1 of the mobile width bug).
              routeOverride alone used to imply "embedded", which was wrong:
              PublicHostRouter also sets routeOverride for a STANDALONE
              paywall on a custom domain (root cause #2) — that path must
              keep the normal max-width:680 obёртки, not this funnel one. */}
          <PublicPaywallPage
            routeOverride={{
              projectId,
              paywallSlug: funnel.paywallSlug,
              // Ревизия пачки 29, п.4: метка «квиз из копии края» доезжает
              // до шага оплаты — раньше она обрывалась на PublicQuiz.
              ...(servedStale ? { servedStale: true } : {}),
            }}
            // Язык воронки нужен плашке, которую показывают ДО тела
            // пейволла (упавший запрос — своего `language` ещё нет).
            funnelLanguage={language}
            /**
             * WEB-2189 (решение владельца 17.09.2026) — НАСТОЯЩИЙ НОМЕР
             * ЭКРАНА для замеров скорости. Раньше страница оплаты ставила
             * себе номер сама и внутри воронки он всегда был «2»: у воронки
             * из пяти экранов шаг оплаты складывался в панели со ВТОРЫМ
             * экраном квиза, и цифра врала, ничего не сломав.
             *
             * `stepNumber + 1` — экран СРАЗУ ЗА последним показанным экраном
             * квиза (`stepNumber` — тот же счётчик пути, что едет в события
             * воронки и в замеры экранов квиза; вторая копия счётчика здесь
             * не заводится). Нумерация общая, с единицы (WEB-2190).
             *
             * ГРАНИЦА ЧЕСТНОСТИ, ПЕРЕПИСАННАЯ ПО ФАКТУ 18.09 (хвост
             * третьего прохода свежего взгляда). Здесь было написано, что у
             * восстановленного прохождения пройденный путь в этой вкладке
             * пуст и номер выходит «2», как и раньше. С той же правки
             * история переходов переживает перезагрузку
             * (quizProgressStorage.ts), поэтому `stepNumber` у
             * восстановленного прохода — настоящая глубина пути, а не
             * единица. То есть число, уезжающее на сервер в событиях и
             * замерах, у восстановленных проходов ИЗМЕНИЛОСЬ и стало
             * честнее: до этого глубина таких проходов занижалась, а
             * пороговые показатели «дошёл до 50/90 %» по ним недосчитывались.
             *
             * Что осталось неточным и почему это допустимо: глубина берётся
             * из ВОССТАНОВЛЕННОЙ истории, а каждое «Назад» её укорачивает —
             * человек, отступивший на шаг и перезагрузившийся, даст номер на
             * единицу меньше прежнего. Хранить отдельный счётчик ради
             * точности значило бы завести в браузере покупателя ещё одно
             * поле; занижение на шаг здесь дешевле.
             */
            funnelStepIndex={stepNumber + 1}
            embedded
            // WEB-785 п.2: оформление — свойство воронки, а не её половинок.
            // Набор меняется в конструкторе только у той части, которую
            // сейчас правят, и части разъезжаются: покупатель шёл по одному
            // пути и на середине видел, что указатель прогресса сменил вид.
            funnelStylePackId={stylePackId}
          />
        </Suspense>
      </PublicErrorBoundary>
    );
  }
  // Заглушка «Thank you» — только для quiz-only (lead-gen) квизов без
  // funnel-контекста (легитимный состав DF6, _funnel-runtime.md §3).
  return (
    <div
      style={{ textAlign: 'center', padding: '40px 16px' }}
      data-testid="quiz-completed"
    >
      <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
      <h2
        style={{
          fontSize: 24,
          fontWeight: 700,
          marginBottom: 8,
          color: 'var(--color-text-primary, inherit)',
        }}
      >
        {/* WEB-785 (возврат QA 01.08, п.7): заглушка была зашита
            по-английски и торчала поверх русской воронки. */}
        {getPublicQuizStrings(language).completedTitle}
      </h2>
      <p style={{ color: 'var(--color-text-secondary, #595959)' }}>
        {getPublicQuizStrings(language).completedText}
      </p>
    </div>
  );
}
